package app.footyguide.web.route

import app.footyguide.core.deeplink.FootyGuideRoute
import app.footyguide.core.deeplink.FootyGuideRouteParser
import app.footyguide.core.theme.ClubCatalog
import kotlinx.browser.window

/**
 * Rotas da aplicação web — History API com URLs limpas (sem #).
 * Cobertura 100% multilíngue para PT (default), EN e ES com suporte a SEO.
 */
sealed class WebRoute {
    abstract val locale: String

    data class Home(override val locale: String = "pt") : WebRoute()
    data class GamesByDate(val date: String, override val locale: String = "pt") : WebRoute()
    data class GameDetail(
        val slug: String,
        val matchId: String? = null,
        override val locale: String = "pt"
    ) : WebRoute()
    data class MyTeam(val teamId: String? = null, override val locale: String = "pt") : WebRoute()
    data class ClubDetail(val clubSlug: String, override val locale: String = "pt") : WebRoute()
    data class Standings(override val locale: String = "pt") : WebRoute()
    data class Channels(val channelId: String? = null, override val locale: String = "pt") : WebRoute()
    data class NotFound(override val locale: String = "pt") : WebRoute()
}

/**
 * Converte o pathname da URL atual para uma WebRoute, extraindo idioma e entidade.
 */
fun parseWebRoute(pathname: String): WebRoute {
    val clean = pathname.trimStart('/').removeSuffix("/")
    val segments = clean.split("/").filter { it.isNotBlank() }

    if (segments.isEmpty()) return WebRoute.Home("pt")

    var locale = "pt"
    var sub = segments

    if (segments[0].equals("en", ignoreCase = true)) {
        locale = "en"
        sub = segments.drop(1)
    } else if (segments[0].equals("es", ignoreCase = true)) {
        locale = "es"
        sub = segments.drop(1)
    }

    if (sub.isEmpty()) return WebRoute.Home(locale)

    val first = sub[0].lowercase()

    when (first) {
        // ── Jogos / Partidas ──────────────────────────────────────
        "jogo", "match", "partido" -> {
            return if (sub.size >= 3) {
                val slug = sub[1]
                val id = sub[2]
                WebRoute.GameDetail(slug = slug, matchId = id, locale = locale)
            } else if (sub.size == 2) {
                val second = sub[1]
                if (second.contains("/")) {
                    val p = second.split("/")
                    WebRoute.GameDetail(slug = p[0], matchId = p.getOrNull(1), locale = locale)
                } else {
                    WebRoute.GameDetail(slug = second, matchId = null, locale = locale)
                }
            } else {
                WebRoute.Home(locale)
            }
        }

        // ── Agenda / Datas ────────────────────────────────────────
        "agenda", "schedule", "calendario" -> {
            if (sub.size >= 2) {
                val dateStr = sub[1]
                if (dateStr.matches(Regex("^\\d{4}-\\d{2}-\\d{2}$"))) {
                    return WebRoute.GamesByDate(date = dateStr, locale = locale)
                }
            }
            return WebRoute.Home(locale)
        }

        // ── Atalhos de Datas (Hoje / Amanhã) ─────────────────────
        "jogos-de-hoje", "matches-today", "partidos-de-hoy" -> {
            val today = js("new Date().toLocaleDateString('sv-SE')") as String
            return WebRoute.GamesByDate(date = today, locale = locale)
        }
        "jogos-de-amanha", "matches-tomorrow", "partidos-de-manana" -> {
            val tomorrow = js("new Date(Date.now() + 86400000).toLocaleDateString('sv-SE')") as String
            return WebRoute.GamesByDate(date = tomorrow, locale = locale)
        }

        // ── Meu Time ──────────────────────────────────────────────
        "meu-time", "my-team", "mi-equipo" -> {
            val team = sub.getOrNull(1)
            return WebRoute.MyTeam(teamId = team, locale = locale)
        }

        // ── Página do Clube ───────────────────────────────────────
        "time", "team", "equipo" -> {
            val club = sub.getOrNull(1) ?: "flamengo"
            return WebRoute.ClubDetail(clubSlug = club, locale = locale)
        }

        // ── Onde Assistir (SEO Search Landing) ────────────────────
        "onde-assistir", "where-to-watch", "donde-ver" -> {
            val target = sub.getOrNull(1) ?: "flamengo"
            if (target.contains("_vs_") || target.contains("_x_") || target.contains("-vs-")) {
                return WebRoute.GameDetail(slug = target, matchId = sub.getOrNull(2), locale = locale)
            }
            return WebRoute.ClubDetail(clubSlug = target, locale = locale)
        }

        // ── Tabelas / Classificação ───────────────────────────────
        "tabelas", "classificacao", "standings", "tables", "posiciones", "clasificacion", "competicao", "competition" -> {
            return WebRoute.Standings(locale)
        }

        // ── Canais de Transmissão ─────────────────────────────────
        "canais", "channels", "canales", "canal", "channel" -> {
            val channel = sub.getOrNull(1)
            return WebRoute.Channels(channelId = channel, locale = locale)
        }

        // ── Institucional & Fallback ─────────────────────────────
        "sobre", "about", "privacidade", "privacy", "privacidad", "termos", "terms", "terminos" -> {
            return WebRoute.Home(locale)
        }

        else -> {
            val matchedClub = ClubCatalog.bySlug(first)
            if (matchedClub != null) {
                return WebRoute.ClubDetail(clubSlug = matchedClub.slug, locale = locale)
            }
            return WebRoute.Home(locale)
        }
    }
}

/**
 * Constantes e builders centralizados de rotas — única fonte da verdade para
 * toda navegação e links na aplicação web.
 */
object WebRoutes {
    const val HOME = "/"
    const val MY_TEAM = "/meu-time"
    const val STANDINGS = "/tabelas"
    const val CHANNELS = "/canais"
    const val SETTINGS = "/configuracoes"
    const val PRIVACY = "/privacidade"
    const val TERMS = "/termos"
    const val ABOUT = "/sobre"

    fun home(locale: String = "pt"): String = WebRoute.Home(locale).toPath()

    fun scheduleByDate(date: String, locale: String = "pt"): String =
        WebRoute.GamesByDate(date = date.trim(), locale = locale).toPath()

    fun club(clubSlug: String, locale: String = "pt"): String {
        val clean = clubSlug.trim().trimStart('/')
        val canonicalSlug = ClubCatalog.bySlug(clean)?.slug ?: clean
        return WebRoute.ClubDetail(clubSlug = canonicalSlug, locale = locale).toPath()
    }

    fun myTeam(teamId: String? = null, locale: String = "pt"): String {
        val canonicalTeam = teamId?.trim()?.trimStart('/')?.takeIf { it.isNotEmpty() }?.let {
            ClubCatalog.bySlug(it)?.slug ?: it
        }
        return WebRoute.MyTeam(teamId = canonicalTeam, locale = locale).toPath()
    }

    fun standings(locale: String = "pt"): String = WebRoute.Standings(locale).toPath()

    fun channels(channelId: String? = null, locale: String = "pt"): String =
        WebRoute.Channels(channelId = channelId?.trim()?.trimStart('/'), locale = locale).toPath()

    fun game(game: app.footyguide.domain.model.Game, locale: String = "pt"): String =
        app.footyguide.web.page.buildGameRoute(game, locale)
}

/**
 * Gera o path limpo (sem #) a partir de uma WebRoute.
 */
fun WebRoute.toPath(): String = when (this) {
    is WebRoute.Home -> if (locale == "pt") "/" else "/$locale"
    is WebRoute.GamesByDate -> when (locale) {
        "en" -> "/en/schedule/$date"
        "es" -> "/es/calendario/$date"
        else -> "/agenda/$date"
    }
    is WebRoute.GameDetail -> {
        val cleanSlug = slug.replace("/", "-")
        val idPart = if (matchId != null) "/$matchId" else ""
        when (locale) {
            "en" -> "/en/match/$cleanSlug$idPart"
            "es" -> "/es/partido/$cleanSlug$idPart"
            else -> "/jogo/$cleanSlug$idPart"
        }
    }
    is WebRoute.MyTeam -> {
        val teamPart = if (teamId != null) "/$teamId" else ""
        when (locale) {
            "en" -> "/en/my-team$teamPart"
            "es" -> "/es/mi-equipo$teamPart"
            else -> "/meu-time$teamPart"
        }
    }
    is WebRoute.ClubDetail -> when (locale) {
        "en" -> "/en/team/$clubSlug"
        "es" -> "/es/equipo/$clubSlug"
        else -> "/time/$clubSlug"
    }
    is WebRoute.Standings -> when (locale) {
        "en" -> "/en/standings"
        "es" -> "/es/posiciones"
        else -> "/tabelas"
    }
    is WebRoute.Channels -> {
        val chPart = if (channelId != null) "/$channelId" else ""
        when (locale) {
            "en" -> "/en/channels$chPart"
            "es" -> "/es/canales$chPart"
            else -> "/canais$chPart"
        }
    }
    is WebRoute.NotFound -> "/"
}

/**
 * Navega usando History API e aciona recomposição do app.
 */
fun navigateTo(path: String) {
    val nav = window.asDynamic().__fgNavigate
    if (nav != null && nav != js("undefined")) {
        nav(path)
    } else {
        val target = if (path.startsWith("/")) path else "/$path"
        if (window.location.pathname != target) {
            window.history.pushState(js("{}"), "", target)
        }
        try {
            window.dispatchEvent(js("new PopStateEvent('popstate')"))
        } catch (_: Throwable) {}
    }
}
